import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { BarChart3, Eye, FileText, Car, Map, PlaneTakeoff, MapPin, RefreshCw } from "lucide-react";
import { api_get } from "../../lib/api";

const TYPE_META = {
    page: { label: "Page", icon: FileText, color: "bg-zinc-100 text-zinc-700" },
    cab: { label: "Cab", icon: Car, color: "bg-blue-100 text-blue-700" },
    package: { label: "Package", icon: Map, color: "bg-emerald-100 text-emerald-700" },
    transfer: { label: "Transfer", icon: PlaneTakeoff, color: "bg-amber-100 text-amber-700" },
    destination: { label: "Destination", icon: MapPin, color: "bg-rose-100 text-rose-700" },
};

const Analytics = () => {
    const [filter, setFilter] = useState("all");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? { limit: 50 } : { type: filter, limit: 50 };
            const [s, v] = await Promise.all([
                api_get("/admin/analytics/summary"),
                api_get("/admin/analytics/views", params),
            ]);
            setSummary(s);
            setRows(v || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load analytics");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="space-y-6" data-testid="admin-analytics-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-widest text-ink-light flex items-center gap-1.5">
                        <BarChart3 className="w-3.5 h-3.5" /> Analytics
                    </div>
                    <h1 className="font-display text-2xl text-ink font-bold mt-1">Top viewed pages & products</h1>
                    <p className="text-sm text-ink-light mt-1">Identify your best-performing destinations, cabs and tour packages.</p>
                </div>
                <button
                    onClick={load}
                    className="px-3 py-1.5 rounded-lg border border-cream-200 bg-white hover:bg-cream-50 text-sm flex items-center gap-1.5"
                    data-testid="analytics-refresh"
                    disabled={loading}
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            {/* Summary */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="analytics-summary">
                    <div className="bg-white border border-cream-200 rounded-xl p-4">
                        <div className="text-xs uppercase tracking-widest text-ink-light">Total views</div>
                        <div className="font-display text-2xl text-ink font-bold mt-1" data-testid="summary-total-views">
                            {summary.total.views.toLocaleString()}
                        </div>
                        <div className="text-xs text-ink-light mt-0.5">{summary.total.uniqueEntities} unique entities</div>
                    </div>
                    {Object.entries(TYPE_META).map(([k, meta]) => {
                        const Icon = meta.icon;
                        const v = summary.byType[k];
                        return (
                            <div key={k} className="bg-white border border-cream-200 rounded-xl p-4">
                                <div className="text-xs uppercase tracking-widest text-ink-light flex items-center gap-1.5">
                                    <Icon className="w-3 h-3" /> {meta.label}
                                </div>
                                <div className="font-display text-2xl text-ink font-bold mt-1">
                                    {(v?.totalViews ?? 0).toLocaleString()}
                                </div>
                                <div className="text-xs text-ink-light mt-0.5">{v?.uniqueEntities ?? 0} unique</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filter pills */}
            <div className="flex flex-wrap gap-2" data-testid="analytics-filter">
                {[["all", "All"], ["page", "Pages"], ["cab", "Cabs"], ["package", "Packages"], ["transfer", "Transfers"], ["destination", "Destinations"]].map(([k, lbl]) => (
                    <button
                        key={k}
                        onClick={() => setFilter(k)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                            filter === k
                                ? "bg-ink text-white border-ink"
                                : "bg-white text-ink-light border-cream-200 hover:border-ink"
                        }`}
                        data-testid={`analytics-filter-${k}`}
                    >
                        {lbl}
                    </button>
                ))}
            </div>

            {/* Top list */}
            <div className="bg-white border border-cream-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-cream-200 flex items-center gap-2 text-sm font-semibold text-ink">
                    <Eye className="w-4 h-4" /> Top {filter === "all" ? "items" : TYPE_META[filter]?.label.toLowerCase() + "s"}
                </div>
                {loading && rows.length === 0 ? (
                    <div className="p-6 text-center text-sm text-ink-light">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-6 text-center text-sm text-ink-light" data-testid="analytics-empty">
                        No views tracked yet. Visit some pages on the public site to populate this list.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-cream-50 text-ink-light text-xs uppercase tracking-widest">
                            <tr>
                                <th className="text-left p-3 font-medium">#</th>
                                <th className="text-left p-3 font-medium">Type</th>
                                <th className="text-left p-3 font-medium">Item</th>
                                <th className="text-right p-3 font-medium">Views</th>
                                <th className="text-left p-3 font-medium hidden md:table-cell">Last view</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                const meta = TYPE_META[r.entityType] || TYPE_META.page;
                                const Icon = meta.icon;
                                return (
                                    <tr key={`${r.entityType}-${r.entityId}`} className="border-t border-cream-200 hover:bg-cream-50/50">
                                        <td className="p-3 text-ink-light font-mono">{i + 1}</td>
                                        <td className="p-3">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
                                                <Icon className="w-3 h-3" /> {meta.label}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-3">
                                                {r.image && (
                                                    <img src={r.image} alt={r.label} className="w-10 h-10 rounded object-cover bg-cream-100" loading="lazy" />
                                                )}
                                                <div className="min-w-0">
                                                    <div className="text-ink font-semibold truncate">{r.label}</div>
                                                    {r.subtitle && <div className="text-xs text-ink-light truncate">{r.subtitle}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-right">
                                            <span className="font-display text-lg font-bold text-ink">{r.views.toLocaleString()}</span>
                                        </td>
                                        <td className="p-3 text-xs text-ink-light hidden md:table-cell">
                                            {r.lastViewed ? new Date(r.lastViewed).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <p className="text-xs text-ink-light">
                Counts are deduplicated per visitor session (sessionStorage) — refreshing a page won&apos;t inflate the count.
            </p>
        </div>
    );
};

export default Analytics;
